import styled from "@emotion/styled";
import { Flex, Select, SelectProps, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { ReactNode, forwardRef, useEffect, useRef, useState } from "react";
import {
  PrinterOption,
  usePrintersAutoComplete,
  usePrintersCode,
} from "./auto-useGetPrintersQuery";

interface PrinterProps extends React.ComponentPropsWithoutRef<"div"> {
  // group: string;
  label: string;
  value: string;
}

export interface PrinterAutoCompleteProps
  extends Partial<SelectProps>,
  Partial<React.RefAttributes<HTMLInputElement>> {
  value?: string | null;
  onChange?: (printerCode: string | null) => void;
  maxDropdownHeight?: number;
  outCode?: string | null;
  query?: any
  width?: string;
}

export const PrinterAutoComplete = (params: PrinterAutoCompleteProps) => {
  const {
    value: printerCode,
    onChange,
    maxDropdownHeight,
    width,
    outCode,
    query,
    ...etcParams
  } = params;

  const [focused, setFocused] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");

  const { data: options } = usePrintersAutoComplete(focused, searchKeyword, JSON.stringify(query));

  const { data: initialOptions } = usePrintersCode(
    !!printerCode,
    printerCode ?? null
  );

  const selectRef = useRef<HTMLInputElement>(null);  // Select 컴포넌트를 참조하기 위한 ref

  const [localOptions, setLocalOptions] = useState<PrinterOption[]>([]);

  useEffect(() => {
    if (options) {
      setLocalOptions(options);
    }
  }, [options]);

  let selectedPrinter = initialOptions?.find((item) => {
    return item.value === printerCode;
  });

  const onChangeHandler = (e: string | null) => {
    selectedPrinter = options?.find((item) => item.value === e);
    onChange && onChange(e);
  };

  const SelectItem = forwardRef<HTMLDivElement, PrinterProps>(
    ({ label: name, value: code, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Flex direction="row" justify="space-between" align="center">
          <div>
            <Text>{name}</Text>
            <Text fz="xs"> (code: {code})</Text>
          </div>
        </Flex>
      </div>
    )
  );

  const handleKeyDown = (e: { key: string; }) => {
    if (e.key === 'Enter') {
      const matchingOption = localOptions?.find((item) => item.value === searchKeyword);
      if (matchingOption) {
        onChangeHandler(matchingOption.value);
        selectRef.current?.blur();  // Select 컴포넌트의 포커스를 잃음
      }
    }
  };

  return (
    <PrinterSelect
      ref={selectRef}  // Select 컴포넌트에 ref 연결
      styles={
        printerCode
          ? {
            input: {
              width: width,
              fontSize: "0.8em",
              paddingBottom: "0.5rem",
            },
          }
          : { input: { width: width } }
      }
      onDropdownOpen={() => setFocused(true)}
      onDropdownClose={() => setFocused(false)}
      clearable
      inputContainer={(children: ReactNode) => (
        <div style={{ position: "relative" }}>
          {children}
          {selectedPrinter?.value && (
            <div
              style={{
                position: "absolute",
                bottom: "0px",
                fontSize: "0.8em",
                color: "#888",
                left: "5px",
              }}
            >
              {"Code: " + selectedPrinter?.value}
            </div>
          )}
        </div>
      )}
      value={printerCode && printerCode}
      itemComponent={SelectItem}
      searchValue={searchKeyword}
      data={[...(options ?? []), ...(initialOptions ?? [])].reduce(
        (unique: PrinterProps[], option: PrinterProps) => {
          return unique.some((u) => u.value === option.value) ||
            option.value === outCode
            ? unique
            : [...unique, option];
        },
        []
      )}
      searchable
      maxDropdownHeight={maxDropdownHeight ?? 150}
      onChange={onChangeHandler}
      onSearchChange={setSearchKeyword}
      rightSection={PrinterInfo({
        printer: selectedPrinter as PrinterOption,
        onChange: onChangeHandler,
      })}
      filter={(value, item) =>
        item?.label?.toLowerCase().includes(value.toLowerCase().trim()) ||
        item?.value?.toLowerCase().includes(value.toLowerCase().trim())
      }
      nothingFound="No options"
      // nothingFound={AddNewLocation({
      //   locationName: searchKeyword,
      //   onChange: onChangeHandler,
      // })}
      {...etcParams}
      onKeyDown={handleKeyDown}
    />
  );
};

const PrinterInfo = (params: {
  printer?: PrinterOption;
  onChange: (printerCode: string | null) => void;
}) => {
  const { printer, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
  };

  return printer?.value ? (
    <PrinterInfoLabel>
      <IconX size="2rem" onClick={clearHandler} />{" "}
    </PrinterInfoLabel>
  ) : null;
};

const PrinterInfoLabel = styled.div`
  font-size: 12px;
  color: #666666;
  // padding-right: 8px;
  padding-left: 0.4rem;
  display: flex;
  align-items: center;
  position: absolute;
`;

const PrinterSelect = styled(Select)`
`;
